import {Animation, Detail, Icon, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import novaMark from '../../../../../../images/nova/nova-mark.svg';
import {getAvailableAgents} from '../../../nova-invocation/novaInvocationStore';
import {failureInvitesRetry, FailurePresentation} from '../../../nova-invocation/FailurePresentation';
import {NovaLabelChip} from '../../../nova-invocation/NovaLabelChip';
import {BasedOnList} from '../../../nova-invocation/BasedOnList';
import {NovaCompactButton} from '../../../nova-invocation/NovaCompactButton';
import {InlineButton, LoaderSlot} from '../../../nova-invocation/novaDsControls';
import {specialistTitleFor} from '../../../nova-invocation/novaSpecialist';
import {RunTimestamp} from './RunTimestamp';
import {RoutingDisclosure} from '../../../nova-invocation/RoutingDisclosure';
import {basedOnItemsFor, isAnswered, outcomeFor} from './novaThreadTurn';
import Person from '../../../person/person';
import {PERSON_IMAGE_SIZES} from '../../../person/personImageSizes';
import {trackNovaOutputCopied} from '../../../../../../tracking/amplitude/novaTaskAgentEvents';
import {profilePicSrc} from '../../../../../../directApi';

export interface NovaConversationTurnProps {
	// The task the turn belongs to, reported with a copy of its answer.
	taskId: string;
	// Handed the answer card's element, for a caller that needs to know when it is on screen.
	answerCardRef?: (element: HTMLDivElement | null) => void;
	run: {
		id: string;
		// The run's raw id, the one the server's run analytics carry. Null on a turn that is not a
		// saved run (a placeholder or a stand-in).
		runId: string | null;
		// Which conversation this run belongs to. Runs sharing a thread are one conversation; a new
		// thread is a new one, which is how changing the agent retires what came before it.
		threadId: string | null;
		agentId: string | null;
		status: string | null;
		outcome: string | null;
		answer: string | null;
		userMessageText: string | null;
		// Who asked. Read from the run rather than from the viewer so a thread several people have
		// replied on names each of them against their own turn.
		actingPerson?: {fullName: string | null; profilePictureId: string | null} | null;
		ranAt: string | null;
		failureState: string | null;
		grounding: {resolved?: ReadonlyArray<{token: string | null; scope: string | null}> | null} | null;
	};
	// Whether this turn shows what the run read. Only the conversation's opening turn does: the
	// sources are the same for every turn of a thread, and repeating them under each answer reads
	// as five separate reads of the same five things.
	showGrounding?: boolean;
	// The thread's own change-agent control, placed inside this card rather than built here. The
	// affordance owns an agent picker anchored to its own button, so it stays where it is mounted
	// and this component only decides where it sits.
	changeAgentAffordance?: React.ReactNode;
	// What the discard control should be called. A thread of one answer discards "Discard"; a
	// conversation with replies in it discards all of them, and says so.
	discardLabel?: string;
	// Offers this turn's own way to retire the whole conversation. Thread-scoped, not turn-scoped:
	// one discarded run retires every turn of the thread it belongs to, which is why a thread hands
	// this to one turn rather than to all of them. Optional and undefined by default, so a turn
	// rendered without it - a read-only task, a thread mid-run, every existing caller - shows no
	// discard control at all rather than one that cannot be honoured.
	onDiscard?: () => void;
	// The reset date of a spent AI allowance, passed through to the failure block.
	resetsOn?: string | null;
	// The way out of a turn with nothing in it. Two callers, one control:
	//  - the temporary out-of-credits notice, which is never saved, so dismissing it is a local
	//    forget and nothing more;
	//  - a saved run that produced nothing, where dismissing clears that one run and leaves every
	//    turn above it on the thread.
	// Both are "take this notice away", neither takes an answer with it, and which of the two this
	// is belongs to the caller rather than to the card.
	//
	// Distinct from onDiscard, which is whole-conversation. A turn is handed one or the other, never
	// both: two ways out of one notice, at two different scopes, with nothing on the card to tell
	// them apart.
	onDismiss?: () => void;
	// Opens the task's reply composer on this conversation. Offered on the newest turn only: a reply
	// continues the thread from where it has got to, so a control on an older turn would promise to
	// answer that turn and do something else.
	onReplyToNova?: () => void;
	// Shown, but refused, with the reason on it — the same distinction the discard and retry pairs
	// draw. Distinct from omitting onReplyToNova, which takes the control away entirely.
	replyDisabled?: boolean;
	replyDisabledReason?: string;
	// Whether this person could start another run on this task at all. The failure block's closing
	// sentence sends the reader to the task's own control, which is wrong for someone that control is
	// withheld from — a locked task, or a person without permission to edit it. Absent means they
	// can, so a caller that does not pass it keeps the sentence it already showed.
	canInvokeAgain?: boolean;
	// Passed straight to the failure block - see its own note. Distinct from canInvokeAgain, which
	// is also false while a block that clears applies.
	retryWithheld?: boolean;
	// Runs this turn again. Thread-scoped knowledge, so the thread decides what "again" means and
	// whether it is possible at all; this turn only places the control, in the action row beside
	// Discard. Optional and undefined by default, so a turn rendered without it keeps the sentence
	// pointing at the task's own control.
	onRetry?: () => void;
	// Shown, but refused, with the reason on it — the same distinction the discard pair draws.
	retryDisabled?: boolean;
	retryDisabledReason?: string;
	// Collapses the whole thread from this turn's own header. Offered on the opening turn only, as
	// the chevron beside the brand name - the same control the header itself reads as. Optional and
	// undefined by default, so a turn rendered without it (a later turn, or a thread whose collapse
	// is withheld for a reason of its own) simply has no chevron.
	onCollapse?: () => void;
	// Rendered as the thread's single summary row: the header alone, with the body and every control
	// under it folded away. The header is the same one the expanded turn carries, so the control that
	// folded the thread and the row reporting it folded are one object rather than two that have to
	// agree.
	collapsed?: boolean;
	// What the summary row says is hidden behind it. Supplied by the thread, which is the only place
	// that can count what it folded away.
	hiddenNote?: string;
	onExpand?: () => void;
	// What the person typed in reply, already separated from the prompt the run saved around it.
	// Decided by the thread, which is the only place that can see the opening run this one repeats.
	// Absent or null means this turn has no reply of its own to show.
	replyText?: string | null;
	// This turn is the run currently in flight: the person's reply is on screen and Nova has not
	// answered yet. The card keeps its shape and shows a spinner where the answer will be, so the
	// thread does not jump when the text arrives.
	//
	// Everything a turn offers is about an answer that exists — copy it, discard it, see what it
	// read — so a pending turn shows none of it. The agent is still named, because the person chose
	// it and it is what they are waiting on.
	pending?: boolean;
}

// Optional chaining for the same reason NovaInvocationControl needs it: this module loads
// transitively through the shared web-components barrel, and a test that mocks
// '@forecast-it/design-system' without providing tokensForThemeProvider would otherwise crash on
// import alone, before any test ran.
const ref = tokensForThemeProvider?.sky?.ref?.color;
const sys = tokensForThemeProvider?.sky?.sys?.color;
const size = tokensForThemeProvider?.sky?.sys?.size;
const font = tokensForThemeProvider?.sky?.sys?.font;

// Nova's card is the design system's Frame, and the person's reply its ColouredContainer in blue.
// Both are read as the values those two components themselves render rather than mounted as the
// components, because neither forwards a className or a data-cy - each destructures the props it
// knows and drops the rest - and these cards are what this feature's own suite and its Cypress
// specs select on. Raised with the design-system team alongside SplitButton.Section, which drops
// them the same way.
//
// So the surfaces below are the components' own tokens, not an approximation of them: Frame's
// neutral-2 under a decorative hairline at radius-3, and ColouredContainer's blue-100.
const SURFACE = sys?.background.neutral['2'] ?? '';
const SURFACE_BORDER = sys?.border.decorative ?? '';
const REPLY_SURFACE = ref?.blue['100'] ?? '';
const CARD_RADIUS = size?.borderradius['3'] ?? '';
const SPACING_200 = tokensForThemeProvider?.sky?.sys?.spacing?.['200'] ?? '8px';
// The header's own type: the brand name, the agent chip and the timestamp are all drawn in it, and
// the folded row's summary copy is drawn in it to match. Hoisted beside the colours rather than
// repeated at each use, the way every other token in this file is read once here.
const DETAIL_1 = font?.detail['1'] ?? '';
// The reader's mark sits a few steps up the same blue scale the panel is drawn from, so the two
// sides of the exchange read as one conversation rather than two unrelated palettes.
const VIEWER_MARK_SURFACE = ref?.blue['300'] ?? '';
const VIEWER_MARK_GLYPH = sys?.text.content.ondark ?? '';

// A run that failed is still Nova speaking, so its card keeps the shape of every other and only
// changes colour - the reader is meant to spot it down a long thread without reading it first.
//
// The one carve-out from this file's "design-system components and colours only" rule, and the
// ticket names it: the system ships no red message container, so these two red tokens are used
// directly rather than approximating one out of something else.
const FAILED_SURFACE = ref?.red['50'] ?? '';
const FAILED_SURFACE_BORDER = ref?.red['100'] ?? '';

// Nova's own card. A design-system Frame carries the neutral surface, the hairline border and the
// radius; the padding is the system's spacing-200, and the gap below is the body's own inset.
//
// The accent stripe this used to draw down its left edge is gone - the thread's shared rule is the
// system's components and colours and nothing of its own, and a stripe is exactly the kind of
// thing that rule is aimed at.
//
// The failed variant stays token-built. It is the one carve-out the ticket names: the system has
// no red message container, so the two red tokens are used directly rather than approximating one.
const AnswerCard = styled.div<{failed?: boolean; isCollapsed?: boolean}>`
	display: flex;
	flex-direction: column;
	gap: ${props => (props.isCollapsed ? '4px' : '8px')};
	flex: 1;
	min-width: 0;
	padding: ${SPACING_200};
	border-radius: ${CARD_RADIUS};
	background: ${props => (props.failed ? FAILED_SURFACE : SURFACE)};
	border: 1px solid ${props => (props.failed ? FAILED_SURFACE_BORDER : SURFACE_BORDER)};
`;

// The reply the user sent, on the untinted side of the same contrast: a turn of the conversation,
// but authored rather than generated.
// The reply the person sent. A design-system ColouredContainer in its blue carries the surface and
// the radius; this only sets the inset, so the two sides of the exchange start their text on the
// same left edge.
const QuestionCard = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-width: 0;
	gap: 4px;
	padding: ${SPACING_200};
	background: ${REPLY_SURFACE};
	border-radius: ${CARD_RADIUS};
`;

// A reply is laid out exactly like an answer - avatar outside, card beside it - so the two sides of
// the conversation share one left edge. Rendered full-width instead, the reply started further left
// than every answer around it and the thread read as two ragged columns rather than one exchange.
const QuestionRow = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 6px;
`;

// One footprint for every mark in this modal, read from Person's own scale rather than copied as a
// number: a comment renders <Person imageSize="medium">, the thread sits directly above those
// comments in the same column, and the mark beside a reply has to match the face opposite it. Taking
// the value from the same place Person takes it means the two cannot drift apart silently.
const AVATAR_SIZE = PERSON_IMAGE_SIZES.medium;

// Matches the answer's mark footprint so both cards begin at the same x, whichever side authored
// the turn.
// The fallback mark, for a turn whose run names no author - a record saved before runs carried one.
// A glyph rather than initials, because there are no initials to draw: nothing identifies who wrote
// it, and the reader's own face there would claim a reply that may not be theirs. The hexagon is cut
// here and filled from the same palette every other avatar in this modal uses.
const QuestionAvatar = styled.div`
	width: ${AVATAR_SIZE}px;
	height: ${AVATAR_SIZE}px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${VIEWER_MARK_SURFACE};
	clip-path: polygon(50% 0%, 100% 27%, 100% 73%, 50% 100%, 0% 73%, 0% 27%);

	svg {
		stroke: ${VIEWER_MARK_GLYPH};
	}
`;

// Person draws initials as a 23px hexagon pushed 2px in from the left of its 24px box, so on its own
// the hexagon sits right of the Nova mark above it. Centred in the box here instead, so the person's
// mark and Nova's share one vertical axis down the thread.
const QuestionAvatarSlot = styled.div`
	width: ${AVATAR_SIZE}px;
	flex-shrink: 0;
	display: flex;
	justify-content: center;

	.person-component-initials-container {
		padding-left: 0;
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
	}
`;

const QuestionHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	flex-wrap: wrap;
	margin-bottom: 2px;
`;

// Who sent the reply, in the style the agent's own name carries at the head of the answer opposite:
// detail/1 with the weight raised. The two are the same thing - the name of whoever is speaking in
// this turn - and the brand name reaches that style by sitting inside a Detail, which this had no
// equivalent of. Without it the name simply inherited the card's body size and was drawn larger
// than the "Nova" it is answering.
const QuestionAuthor = styled.span`
	${DETAIL_1}
	font-weight: 700;
`;

const TurnStack = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const AnswerRow = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 6px;
`;

// The mark carries its own rounded gradient tile, so it needs no wrapper to sit on - only a fixed
// square footprint so the header beside it cannot squash it.
const NovaMarkImage = styled.img`
	width: ${AVATAR_SIZE}px;
	height: ${AVATAR_SIZE}px;
	flex-shrink: 0;
`;

const HeaderRow = styled.div<{isCollapsed?: boolean}>`
	display: flex;
	align-items: center;
	gap: 4px;
	/* Wraps while expanded, where the header can run long beside a narrow modal. Collapsed it must
	   not: the whole row is one line by definition, and a wrap would drop the hidden-count and Show
	   under the name instead of holding them at the far edge. */
	flex-wrap: ${props => (props.isCollapsed ? 'nowrap' : 'wrap')};
	${props => props.isCollapsed && 'cursor: pointer;'}
`;

// Pushed to the far edge, away from the name and agent that open the row: what is hidden is a
// property of the folded thread, not another attribute of the turn speaking. One element holding
// both halves rather than two loose ones, so the space between the count and Show is a decision of
// its own rather than whatever gap the header happens to use for the name, the agent and the time.
const HiddenSummary = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	margin-left: auto;
	/* Holds its full width while there is room and gives way when there is not. Refusing to shrink
	   outright let a long note - a translation runs well past the English, and the row cannot wrap
	   while it is folded - carry the summary past the card's right edge and clip Show off the end,
	   which is the one part of the row that has to stay reachable. */
	flex: 0 1 auto;
	min-width: 0;
`;

// detail/1, the same style the brand name and the timestamp at the other end of this row are drawn
// in - they are all one line of header copy, and this half was a step larger. Not only wider: the
// larger style carries a 20px line box where everything beside it carries 16px, so it set the row's
// height on its own and sat visibly off the line the rest of the header shared.
const HiddenNote = styled.span`
	${DETAIL_1}
	color: ${sys?.text.content.auxiliary ?? ''};
	/* The half that gives way when the row runs out of width, trailing off rather than pushing Show
	   past the edge. It is the expendable half: it restates a count the reader can also get by
	   opening the thread, where Show is the only way to open it. */
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

// A plain text affordance rather than a design-system Button: the whole header already reads as the
// control, and a bordered button inside a one-line summary row would out-weigh the row it sits in.
// Sized with the note beside it, and carried a step heavier so it reads as the thing to press.
// Never the half that gives way when the folded row runs out of width - see the note beside it.
const ShowSlot = styled.span`
	flex: none;
	white-space: nowrap;
`;

// Who is speaking, before the agent that spoke for them. Heavier than the design system's own
// 12px style, which is its lightest weight: the name is the anchor of the header, and everything
// after it - the agent, the time - is an attribute of what it introduces.
const BrandName = styled.span`
	font-weight: 700;
`;

// The disclosure brings a flex row of its own, which would otherwise take the header's spare
// width and push everything after it to the far edge. The agent and the time belong beside the
// name, as one phrase.
const AgentSlot = styled.div`
	flex: none;
`;

// The chevron the whole header reads as: it turns to point at the collapsed state, so the control
// and the state it reports are the same object.
const CollapseToggle = styled.button<{isCollapsed?: boolean}>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 18px;
	height: 18px;
	flex: none;
	padding: 0;
	border: none;
	background: none;
	color: ${sys?.text.content.auxiliary ?? ''};
	cursor: pointer;
	/* Turns to point at the state it reports: down while the thread is open, right once folded. */
	transform: rotate(${props => (props.isCollapsed ? '-90deg' : '0deg')});
	transition: transform 0.15s;

	svg path {
		fill: ${sys?.text.content.auxiliary ?? ''};
	}

	&:focus-visible {
		outline: 2px solid ${sys?.border.focusring ?? ''};
		outline-offset: 1px;
	}
`;

// The card's action row, under everything else the card shows.
const CardRow = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	flex-wrap: wrap;
	/* No margin of its own. The card is a flex column with its own gap, so a margin here was added to
	   that gap rather than replacing it, and the actions sat twice as far below the answer as every
	   other pair of rows in the card is apart. */
`;

// Discard sits at the far end of the action row, away from the actions that add something. It is
// the only control in the card that takes the conversation away.
// Discard sits at the far end of the row, away from the actions that add something — except on a
// failed turn, where the row holds only the way back and the way out. There is nothing to hold it
// away from there, and a button pinned to the far edge of an otherwise empty row reads as
// unrelated to the one beside it rather than as its alternative.
// The way out sits at the far end of the row, away from the actions that add to the answer -
// including on a failed turn, where Try again is the only thing beside it. The two are alternatives
// rather than a pair, and the design separates them for that reason: the destructive one is not
// somewhere a hand aiming for Try again can land by a few pixels.
const DiscardSlot = styled.div`
	margin-left: auto;
`;

// How long the copy control states that it copied before returning to offering the copy again.
// Long enough to be read, short enough that the control is back to its normal label before the
// person's next glance at it.
const COPIED_LABEL_MS = 1500;

// The answer is free text from a model, and the newlines it wrote are the whole of the formatting it
// has. pre-wrap keeps them without introducing a renderer that would then also have to be trusted
// with the content.
const AnswerText = styled.div`
	white-space: pre-wrap;
`;

// The same spinner the task's own control shows while a run is working, so a person watching either
// one sees the same thing turning. Sized to the answer text's line rather than the control's chip:
// it stands in for the first line of an answer.
// The loader and the words beside it are spaced apart, not butted together: with no gap the
// spinner's edge touched the first letter.
const AnswerPending = styled.div`
	display: flex;
	align-items: center;
	gap: ${SPACING_200};
	min-height: 20px;
`;

// A run's outcome is only meaningful once the run has actually finished. Reading outcomeFor() on
// a run still in flight would tell a lie: novaThreadTurn's outcomeFor treats "not one of the three
// terminal values" as a generic failure (the honest reading for a *saved* record that ought to have
// a terminal outcome), but a running turn has no terminal outcome yet by design, not by accident —
// see the "verify state, do not infer it" rule this guard exists to satisfy. Gating on the outcome
// field itself, rather than on `status === 'RUNNING'`, means an in-between status this component
// hasn't been told about still renders no failure block instead of a fabricated one.
function hasTerminalOutcome(outcome: string | null): boolean {
	return outcome === 'SUCCEEDED' || outcome === 'FAILED' || outcome === 'CANCELLED';
}

// Resolves the id the record saved into the name a person should read. Three distinct cases, all
// handled honestly rather than by rendering blank:
//  - the agent is still in the viewer's available list: use its current title.
//  - the agent id is set but absent from that list: an agent a user cannot run is omitted from the
//    list rather than flagged, so absence cannot distinguish "un-deployed since this run" from any
//    other reason it is missing - the raw id is the only thing this side can still show truthfully.
//  - no agent id was saved on the run at all: state that plainly instead of showing nothing.
function resolveAgentTitle(agentId: string | null, formatMessage: ReturnType<typeof useIntl>['formatMessage']): string {
	if (!agentId) return formatMessage({id: 'nova_thread.turn.unknown_agent'});
	return getAvailableAgents().find(agent => agent.id === agentId)?.title ?? agentId;
}

// One saved run, rendered as a turn: the question that produced it (when the run saved one - an
// opening turn's prompt is carried entirely by the per-run input, so it has no separate question to
// show), then Nova's answer, attributed to the agent that produced it.
export const NovaConversationTurn = (props: NovaConversationTurnProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const {run} = props;
	// Whether this card's copy control is currently saying it copied. Held here rather than
	// announced through a toast: the confirmation belongs on the control that was pressed, because a
	// long thread can hold several answers and a floating message would not say which one was taken.
	const [hasCopied, setHasCopied] = useState(false);
	const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Clears the pending revert on unmount - the modal closing mid-confirmation would otherwise land
	// a state write on a component that no longer exists.
	useEffect(() => {
		return () => {
			if (copiedTimeoutRef.current !== null) clearTimeout(copiedTimeoutRef.current);
		};
	}, []);

	const answerText = run.answer;
	const {runId} = run;
	const {taskId} = props;
	const handleCopy = useCallback(() => {
		if (!answerText) return;
		// Reported on the click, ahead of the clipboard write: a refused write is still someone taking
		// the output, and the modal may close before the write settles.
		trackNovaOutputCopied({runId, taskId});
		// Optional-chained, and the confirmation is deferred until the write actually resolves: the
		// clipboard API is absent outside a secure context and can be refused by permission, and a
		// control that says "Copied" over an empty clipboard is worse than one that says nothing.
		//
		// The rejection is caught rather than left to float. writeText rejects on a denied
		// permission and on a document that does not hold focus, and an uncaught rejection there is
		// an unhandled promise rejection - a console error in production, and a full-screen overlay
		// in a dev build - over something the control can simply decline to confirm. Warned rather
		// than swallowed silently, since nothing else records that the copy did not happen.
		void navigator.clipboard
			?.writeText(answerText)
			.then(() => {
				setHasCopied(true);
				if (copiedTimeoutRef.current !== null) clearTimeout(copiedTimeoutRef.current);
				copiedTimeoutRef.current = setTimeout(() => setHasCopied(false), COPIED_LABEL_MS);
			})
			.catch((error: unknown) => {
				// eslint-disable-next-line no-console
				console.warn('the answer could not be copied to the clipboard:', error);
			});
	}, [answerText, runId, taskId]);

	const agentTitle = resolveAgentTitle(run.agentId, formatMessage);
	// Named only when it is a specialist: the card's own header already says Nova, so repeating the
	// default agent's name under it says nothing the reader does not have.
	const pendingSpecialist = specialistTitleFor(run.agentId);
	// One summary row stands in for the whole folded thread, so everything below the header is
	// withheld — not merely the answer text, but the sources, the failure block and every control.
	const isCollapsed = !!props.collapsed;
	const isPending = !!props.pending;
	// A pending turn has no answer yet by definition, so every branch below that asks "is there an
	// answer" must say no — otherwise a run whose record already carries one (a re-render mid-flight)
	// would show the previous answer under the spinner.
	const answered = !isPending && isAnswered(run);
	// Three states, not two. A run that names nobody has an author who cannot be found - deleted
	// since - and says so. A run that names a person who has no name on file is a different thing
	// entirely: they exist, they wrote this, and the account simply holds no name for them, which is
	// common enough for imported and API-created people that calling them deleted would be wrong far
	// more often than it would be right.
	const replyAuthor = run.actingPerson ?? null;
	const replyAuthorName = replyAuthor?.fullName || null;
	const replyAuthorLabel =
		replyAuthorName ??
		formatMessage({id: replyAuthor ? 'nova_thread.turn.reply_author_unnamed' : 'nova_thread.turn.reply_author_unknown'});
	// Whether this turn is a run that went wrong. Read from the saved outcome rather than from the
	// absence of an answer: a run still in flight also has no answer, and colouring that one as a
	// failure would report an ending it has not reached.
	//
	// FAILED only, and not every FAILED. A cancelled run also has no answer and also renders a block
	// saying so, but the person stopped it themselves - there is no fault to flag and nothing they
	// did not expect, and the palette the product reserves for something going wrong would say
	// otherwise. Its copy still states what happened, on the panel every other turn uses.
	//
	// An exhausted credit pool is saved as FAILED too, but FailurePresentation states plainly that it
	// is "not a failure, and not offered as one" - it reads as an allowance that will come back, with
	// the date it does. Asked through the same predicate the retry below uses, so the colour of the
	// card and the presence of a way out of it cannot disagree about whether this went wrong.
	const failed = !isPending && run.outcome === 'FAILED' && failureInvitesRetry(outcomeFor(run));
	// A run that ended with nothing to show for itself, whatever the reason. Broader than `failed`
	// above, which is only about colour: an exhausted credit pool is not drawn as a failure, but it
	// still left no answer, and replying to it or changing its agent would start a run refused the
	// same way until the pool resets. So the controls ask this, and the palette asks `failed`.
	const producedNothing = !isPending && run.outcome === 'FAILED';
	// Whether this turn shows a way to run the work again. Two questions, both of which have to say
	// yes: the thread has to have given this turn something to run (an older turn, a read-only task
	// and a reply with nothing left to resend all arrive with no onRetry), and the failure itself
	// has to be one a retry could get past — an exhausted credit pool is not.
	const retryOffered =
		!!props.onRetry && !isPending && hasTerminalOutcome(run.outcome) && failureInvitesRetry(outcomeFor(run));

	return (
		// Named apart from a saved turn deliberately: `nova-thread-turn-*` means a run the server has a
		// record of, and several callers enumerate exactly that to ask what the conversation contains.
		// A turn still waiting on its answer is not one of those, and answering to the same name would
		// make it one.
		<TurnStack
			data-cy={
				isPending ? 'nova-thread-pending' : isCollapsed ? 'nova-thread-collapsed-row' : `nova-thread-turn-${run.id}`
			}
		>
			{props.replyText && (
				<QuestionRow>
					<QuestionAvatarSlot>
						{replyAuthor ? (
							// The same avatar a comment on this task shows for the same person, so one face
							// means one person wherever the modal shows them.
							<Person
								cy="nova-thread-reply-avatar"
								name={replyAuthorLabel}
								showName={false}
								showRole={false}
								imageSize="medium"
								// The whole hexagon, points and all - see Person's `unclipped`.
								unclipped
								imageSrc={profilePicSrc(props.run.actingPerson?.profilePictureId)}
							/>
						) : (
							<QuestionAvatar data-cy="nova-thread-reply-mark">
								{/* aria-hidden: the card's header names the author in words right beside this. */}
								<svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2" aria-hidden="true">
									<circle cx="12" cy="8.5" r="3.3" />
									<path d="M5.5 19.5c0-3.3 2.9-5.2 6.5-5.2s6.5 1.9 6.5 5.2" strokeLinecap="round" />
								</svg>
							</QuestionAvatar>
						)}
					</QuestionAvatarSlot>
					<QuestionCard data-cy="nova-thread-question">
						<QuestionHeader>
							<QuestionAuthor data-cy="nova-thread-reply-author">{replyAuthorLabel}</QuestionAuthor>
							{/* Says which conversation the reply belongs to, in the same chip the agent's name is
							    drawn in on the answer opposite - the two are one label, one per side of the
							    exchange, and only the tint under them differs. */}
							<NovaLabelChip tone="user">{formatMessage({id: 'nova_thread.turn.reply_tag'})}</NovaLabelChip>
						</QuestionHeader>
						<Text size="2">{props.replyText}</Text>
					</QuestionCard>
				</QuestionRow>
			)}
			<AnswerRow data-cy="nova-thread-answer">
				<NovaMarkImage src={novaMark} alt="" data-cy="nova-thread-nova-mark" />
				{/* Two markers on purpose: the first is how a test reaches this panel in any state, the
				    second is how it asks whether the panel is the failed one. Finding the card by its
				    position in the row instead would pass or fail on the markup around it. */}
				<AnswerCard
					ref={props.answerCardRef}
					failed={failed}
					isCollapsed={isCollapsed}
					data-cy="nova-thread-answer-card"
					data-failed={failed ? 'true' : undefined}
				>
					<HeaderRow
						isCollapsed={isCollapsed}
						onClick={isCollapsed ? props.onExpand : undefined}
						data-cy="nova-thread-answer-header"
					>
						{(props.onCollapse || isCollapsed) && (
							<CollapseToggle
								type="button"
								isCollapsed={isCollapsed}
								onClick={isCollapsed ? props.onExpand : props.onCollapse}
								aria-expanded={!isCollapsed}
								aria-label={formatMessage({
									id: isCollapsed ? 'nova_thread.collapse.expand_action' : 'nova_thread.collapse.action',
								})}
								data-cy="nova-thread-collapse-toggle"
							>
								<Icon icon="chevronDown" size="s" aria-hidden="true" />
							</CollapseToggle>
						)}
						<Detail data-cy="nova-thread-brand">
							<BrandName>{formatMessage({id: 'nova_thread.collapse.brand_label'})}</BrandName>
						</Detail>
						{/* The agent, named on its own chip and nothing more. The disclosure's optional
						    "why" line is left off deliberately: there is no automatic routing and no
						    account default, so every run is one the person chose, and a sentence saying
						    so under every answer in a long thread states the same thing over and over. */}
						<AgentSlot>
							<RoutingDisclosure agentName={agentTitle} />
						</AgentSlot>
						{run.ranAt && <RunTimestamp ranAt={run.ranAt} data-cy="nova-thread-time" />}
						{isCollapsed && (
							<HiddenSummary>
								<HiddenNote data-cy="nova-thread-collapsed-count">{props.hiddenNote}</HiddenNote>
								<ShowSlot>
									<InlineButton
										emphasis="low"
										useButtonProps
										noMargin
										onPress={() => props.onExpand?.()}
										data-cy="nova-thread-collapsed-show"
									>
										{formatMessage({id: 'nova_thread.collapse.expand_action'})}
									</InlineButton>
								</ShowSlot>
							</HiddenSummary>
						)}
					</HeaderRow>
					{!isCollapsed && isPending && (
						<AnswerPending data-cy="nova-thread-answer-pending" role="status" aria-live="polite">
							{/* The same loader the task's own Working control shows, so a person watching
							    either one sees the same thing turning. It holds still for a reader who has
							    asked for reduced motion, which the hand-drawn spinner it replaces did not. */}
							<LoaderSlot $size={16} aria-hidden="true">
								<Animation animation="loader" />
							</LoaderSlot>
							{/* Said in words beside it rather than left to the spinner alone: a reader with no
							    spinner to look at got an empty label before, and a sighted one had to infer
							    which agent they were waiting on. */}
							<Detail color="medium" aria-label={formatMessage({id: 'nova_thread.turn.pending'})}>
								{pendingSpecialist
									? formatMessage(
											{id: 'nova_invocation.promo.working_specialist'},
											{agent: pendingSpecialist}
									  )
									: formatMessage({id: 'nova_invocation.control.status_working'})}
								{/* A reader who cannot act is watching somebody else's run, with no Stop and
								    nothing to do but wait, so the line says what waiting gets them. Somebody
								    who can act has the controls to answer that for themselves. */}
								{props.retryWithheld ? ` ${formatMessage({id: 'nova_thread.turn.pending_read_only'})}` : ''}
							</Detail>
						</AnswerPending>
					)}
					{!isCollapsed && answered && (
						<AnswerText data-cy="nova-thread-answer-text">
							<Text size="2">{run.answer}</Text>
						</AnswerText>
					)}
					{!isCollapsed && answered && props.showGrounding && <BasedOnList items={basedOnItemsFor(run.grounding)} />}
					{!isCollapsed && !isPending && hasTerminalOutcome(run.outcome) && (
						<FailurePresentation
							outcome={outcomeFor(run)}
							ranAt={run.ranAt}
							resetsOn={props.resetsOn}
							retryOffered={retryOffered}
							canInvokeAgain={props.canInvokeAgain}
							retryWithheld={props.retryWithheld}
						/>
					)}
					{/* One row for everything this turn can be acted on with, so the controls read as a
					    set rather than as separate offers stacked down the card. What is in it is a
					    property of the turn's place in the thread, decided by the thread and arriving
					    here as props — reply, change agent and discard on the newest turn, copy
					    wherever there is an answer.

					    Except on a turn that failed, which offers the way back and the way out and
					    nothing else. Replying continues a conversation that produced nothing to
					    continue from, and changing the agent is a decision about a thread this run
					    never added to — both read as alternatives to Try again when neither is one.
					    Decided here rather than by the thread because this is where the card already
					    reads its own outcome to pick its palette; one answer, one place. */}
					{!isCollapsed &&
						!isPending &&
						(answered ||
							retryOffered ||
							(!producedNothing && props.changeAgentAffordance) ||
							props.onDiscard ||
							props.onDismiss) && (
							<CardRow data-cy="nova-thread-actions">
								{/* The way back, first in the row: on a failed turn it is the only thing to
							    do, and everywhere else it does not appear at all. Drawn as the same
							    outlined control Reply to Nova is, which is what the two have in common -
							    each is the step that carries the conversation on from the card it sits
							    in. A filled button here made the failed card the one place in the thread
							    where an action was drawn in a weight nothing else uses. */}
								{retryOffered && props.onRetry && (
									<NovaCompactButton
										tone="secondary"
										onPress={props.onRetry}
										disabled={props.retryDisabled}
										title={props.retryDisabled ? props.retryDisabledReason : undefined}
										data-cy="nova-failure-retry"
									>
										{formatMessage({id: 'nova_invocation.failure.try_again'})}
									</NovaCompactButton>
								)}
								{/* Only offered where there is an answer to put on the clipboard. A failed
							    turn has nothing to copy, and a control that copies an empty string is
							    indistinguishable from one that is broken. */}
								{answered && (
									<NovaCompactButton tone="secondary" onPress={handleCopy} data-cy="nova-thread-copy">
										{formatMessage({
											id: hasCopied ? 'nova_thread.actions.copied' : 'nova_thread.actions.copy',
										})}
									</NovaCompactButton>
								)}
								{/* A failed turn offers no Change agent control: there is no answer on it to run
								    a different agent against. The list itself is mounted by the thread, not
								    here, so withholding this button no longer takes it away. */}
								{!producedNothing && props.changeAgentAffordance && (
									<span data-cy="nova-thread-change-agent">{props.changeAgentAffordance}</span>
								)}
								{/* Named from the composer's own mode chip rather than a second key saying the
							    same words: this button switches the composer into that mode, so the two have
							    to agree, and one key is what keeps them agreeing in every locale. */}
								{!producedNothing && props.onReplyToNova && (
									<NovaCompactButton
										tone="primary"
										leftIcon="sparkle"
										onPress={props.onReplyToNova}
										disabled={props.replyDisabled}
										title={props.replyDisabled ? props.replyDisabledReason : undefined}
										data-cy="nova-thread-reply"
									>
										{formatMessage({id: 'nova_thread.reply.mode_reply'})}
									</NovaCompactButton>
								)}
								{props.onDiscard && (
									<DiscardSlot>
										<NovaCompactButton
											tone="quiet"
											leftIcon="trash"
											onPress={props.onDiscard}
											data-cy="nova-thread-discard"
										>
											{props.discardLabel ?? formatMessage({id: 'nova_thread.actions.discard'})}
										</NovaCompactButton>
									</DiscardSlot>
								)}
								{props.onDismiss && (
									<DiscardSlot>
										<NovaCompactButton tone="quiet" onPress={props.onDismiss} data-cy="nova-thread-dismiss">
											{formatMessage({id: 'nova_thread.actions.dismiss'})}
										</NovaCompactButton>
									</DiscardSlot>
								)}
							</CardRow>
						)}
				</AnswerCard>
			</AnswerRow>
		</TurnStack>
	);
};
